'use client'

import { useEffect, useRef, useState, type RefObject } from 'react'

/** Shared continuous rail: pointer hold/drag, release to resume, wheel, arrows,
 * hover and keyboard focus. A repeat starts at [data-rail-repeat].
 *
 * FRIDGE-1093 (owner, on a phone: "Hard to interrupt And slide"; docs/Design.md §22.3 — a
 * touch pauses, a swipe takes over, releasing resumes, a marker shows position, nothing is
 * cut off on both sides at rest): a touch hold pauses the drift and resumes only after
 * TOUCH_RESUME_MS, not the mouse's 600 ms; a drag, a touch release and the arrows all settle
 * onto the nearest card start (the children of [data-rail-first]) with a short ease, so a card
 * is fully in view at rest; and `progressRef` receives a 0–1 position (as scaleX) every frame
 * while visible for the bar MovingRailControls draws. Reduced motion keeps the arrows, the swipe and the
 * settle; only the drift stops. */
export const TOUCH_RESUME_MS = 4000
export const MOUSE_RESUME_MS = 600
/** FRIDGE-1502 (N2): how long a tap leaves the rail still before it rests on a whole card.
 * A phone sends the tap's click after the finger lifts, a frame or more later (later still on
 * a slow phone). Settling at the release moved the rail under the finger first, so a Save tap on
 * the homepage price-drop strip opened a neighbouring product: 1 of 6 taps at normal load on a
 * 4x-slowed phone, 10/14/2026. The drift is already paused for TOUCH_RESUME_MS, so the rail
 * holds still until the settle. */
export const TAP_SETTLE_MS = 500
export function useMovingRail<T extends HTMLElement>(ref: RefObject<T | null>, ready: unknown, speed = 24, interactionRef?: RefObject<HTMLElement | null>) {
  const [paused, setPaused] = useState(false)
  const pausedRef = useRef(false)
  const progressRef = useRef<HTMLElement | null>(null)
  const moveRef = useRef<(direction: number) => void>(() => {})
  useEffect(() => {
    const rail = ref.current
    if (!rail) return
    const interaction = interactionRef?.current ?? rail
    const motion = matchMedia('(prefers-reduced-motion: reduce)')
    let reduced = motion.matches
    let frame: number | null = null
    let disposed = false, settleOnResume = false
    let previous = 0, position = rail.scrollLeft, period = 0, resumeAt = 0
    let pointer: number | null = null, startX = 0, startY = 0, startPosition = 0, dragged = false, touch = false
    let target: number | null = null, starts: number[] = [], settleAt = 0
    let suppressClick = false, keyboardFocused = interaction.contains(document.activeElement), visible = false, hovered = false
    const old = { behavior: rail.style.scrollBehavior, snap: rail.style.scrollSnapType, touch: rail.style.touchAction }
    rail.style.scrollBehavior = 'auto'
    rail.style.scrollSnapType = 'none'
    rail.style.touchAction = 'pan-y pinch-zoom'
    const measure = () => {
      if (disposed || !visible || document.hidden) return
      const repeat = rail.querySelector<HTMLElement>('[data-rail-repeat]')
      const first = rail.querySelector<HTMLElement>('[data-rail-first]')
      period = repeat && first ? repeat.getBoundingClientRect().left - first.getBoundingClientRect().left : 0
      // Card starts, measured from the first set's left edge — the settle points.
      const left = first ? first.getBoundingClientRect().left : 0
      starts = first && first.children ? Array.from(first.children as ArrayLike<Element>, (c) => c.getBoundingClientRect().left - left) : []
    }
    const progress = () => {
      const el = progressRef.current
      if (!el) return
      const max = rail.scrollWidth - rail.clientWidth
      const span = period > 0 && max >= period ? period : max
      const frac = span > 0 ? (((position % span) + span) % span) / span : 0
      el.style.transform = `scaleX(${frac.toFixed(4)})`
    }
    /** Nearest card start at or around `from`, in the same loop lap; null when nothing is measured. */
    const nearest = (from: number, direction = 0) => {
      if (!starts.length) return null
      const max = rail.scrollWidth - rail.clientWidth
      const span = period > 0 && max >= period ? period : 0
      const lap = span ? Math.floor(from / span) * span : 0
      const cands = span ? [...starts.map((x) => lap + x), ...starts.map((x) => lap + span + x), ...starts.map((x) => lap - span + x)] : starts
      const pool = direction > 0 ? cands.filter((x) => x > from + 1) : direction < 0 ? cands.filter((x) => x < from - 1) : cands
      if (!pool.length) return null
      const best = pool.reduce((a, b) => (Math.abs(b - from) < Math.abs(a - from) ? b : a))
      return span ? best : Math.max(0, Math.min(max, best))
    }
    const settle = (direction = 0) => { position = rail.scrollLeft; target = nearest(position, direction) }
    const write = (next: number) => {
      const max = rail.scrollWidth - rail.clientWidth
      // Only fold at a seam if a complete viewport can still be filled.
      if (period > 0 && max >= period) {
        const folded = ((next % period) + period) % period
        // FRIDGE-1148 (owner: "super twittchy and out of control", on every rail carrying
        // the progress bar). `nearest` picks a settle target out of whichever lap the step
        // landed in, so a forward arrow at the seam targets `lap + span + x`, past `period`.
        // Folding the position without folding the target left the ease chasing a number the
        // folded position could never reach: |diff| never dropped under 0.5, `target` never
        // cleared, and because it gates the drift branch below the row swept the whole loop
        // every few frames for good (measured live: 1789 px per frame against a 0.4 px
        // drift). Carry the target across by the same whole laps, and direction is kept --
        // folding `diff` to the shortest way round would send a forward arrow backwards on a
        // rail whose loop is shorter than twice the viewport.
        // `Math.round` is load-bearing: ((next % period) + period) % period is an ulp off
        // `next` for a non-integer `next`, so a raw difference would nudge the target every
        // frame even with no fold. Rounding the lap count keeps the no-fold case exactly 0.
        const laps = Math.round((next - folded) / period)
        if (target !== null && laps !== 0) target -= laps * period
        next = folded
      }
      else next = Math.max(0, Math.min(max, next))
      position = next
      rail.scrollLeft = position
    }
    const tick = (now: number) => {
      frame = null
      // No geometry reads, scroll writes or progress writes outside the viewport
      // or while the document is hidden, including a pending card settle.
      if (disposed || !visible || document.hidden) return
      const dt = previous ? Math.min(now - previous, 50) / 1000 : 0
      previous = now
      if (settleAt && now >= settleAt && pointer === null) {
        settleAt = 0
        if (target === null) settle()
      }
      if (target !== null && pointer === null) {
        // Ease onto the card start (also under reduced motion — this is a settle, not a drift).
        const diff = target - position
        if (Math.abs(diff) < 0.5) { write(target); target = null } else write(position + diff * Math.min(1, dt * 12))
      } else if (visible && !document.hidden && !reduced && !pausedRef.current && !keyboardFocused && !hovered && pointer === null && now >= resumeAt) {
        if (Math.abs(rail.scrollLeft - position) > 1) position = rail.scrollLeft
        write(position + speed * dt)
      }
      progress()
      frame = requestAnimationFrame(tick)
    }
    const syncVisibility = () => {
      if (disposed) return
      if (!visible || document.hidden) {
        if (frame !== null) cancelAnimationFrame(frame)
        frame = null
        previous = 0
        // A tab switch may prevent the matching pointerup from arriving.
        if (document.hidden && pointer !== null) {
          if (rail.hasPointerCapture?.(pointer)) rail.releasePointerCapture(pointer)
          suppressClick = dragged
          pointer = null
          settleOnResume = dragged || touch
          resumeAt = performance.now() + (touch ? TOUCH_RESUME_MS : MOUSE_RESUME_MS)
        }
        return
      }
      if (frame === null) {
        previous = 0
        position = rail.scrollLeft
        measure()
        if (settleOnResume) { settle(); settleOnResume = false }
        progress()
        frame = requestAnimationFrame(tick)
      }
    }
    const down = (e: PointerEvent) => {
      if (!e.isPrimary || e.button !== 0) return
      suppressClick = false
      pointer = e.pointerId; startX = e.clientX; startY = e.clientY; startPosition = rail.scrollLeft; dragged = false
      touch = e.pointerType === 'touch'; target = null; settleAt = 0
    }
    const move = (e: PointerEvent) => {
      if (pointer !== e.pointerId) return
      const dx = e.clientX - startX, dy = e.clientY - startY
      if (!dragged && Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy)) {
        dragged = true; rail.setPointerCapture?.(e.pointerId)
      }
      if (dragged) { e.preventDefault(); write(startPosition - dx) }
    }
    const up = (e: PointerEvent) => {
      if (pointer !== e.pointerId) return
      suppressClick = dragged
      if (rail.hasPointerCapture?.(e.pointerId)) rail.releasePointerCapture(e.pointerId)
      pointer = null; resumeAt = performance.now() + (touch ? TOUCH_RESUME_MS : MOUSE_RESUME_MS)
      // A swipe or a touch hold ends on a whole card, never cut off on both sides. A
      // release that did not drag settles after TAP_SETTLE_MS, once its click has landed.
      if (dragged) settle()
      else if (touch || reduced) settleAt = performance.now() + TAP_SETTLE_MS
    }
    const click = (e: MouseEvent) => {
      if (suppressClick) { e.preventDefault(); e.stopPropagation(); suppressClick = false }
    }
    const focus = (e: FocusEvent) => {
      // FRIDGE-1610: a tap or click focuses a button or link too (Chrome for Android keeps a
      // tapped Save button focused), and counting that as keyboard focus stopped the drift
      // until the shopper tapped elsewhere. Only keyboard focus (:focus-visible) pauses the
      // drift and is brought into view; a tap is left to the touch rules above (paused for
      // TOUCH_RESUME_MS after the release, then the drift resumes: docs/Design.md 22.3 rule 2).
      // Where the platform cannot tell, focus still pauses, as before.
      const focused = e.target as Element | null
      if (focused && typeof focused.matches === 'function') {
        let keyboard = true
        try { keyboard = focused.matches(':focus-visible') } catch { keyboard = true }
        if (!keyboard) return
      }
      keyboardFocused = true
      // FRIDGE-1508 (K12): focus pauses the drift, but nothing brought the
      // focused card into view, so a frame already moving the rail could leave
      // it outside (3 of about 1,948 strip Tab stops at 1280 on 10/14/2026).
      // The focused element is moved fully inside the rail and any settle in
      // flight is dropped, so it stays where the reader can see it.
      const el = e.target as Element | null
      if (!el || typeof el.getBoundingClientRect !== 'function' || !rail.contains(el)) return
      const r = rail.getBoundingClientRect(), b = el.getBoundingClientRect()
      const shift = b.left < r.left ? b.left - r.left : b.right > r.right ? b.right - r.right : 0
      if (shift !== 0) { target = null; write(rail.scrollLeft + shift) }
    }
    const blur = (e: FocusEvent) => { if (!interaction.contains(e.relatedTarget as Node)) keyboardFocused = false }
    const enter = (e: PointerEvent) => { if (e.pointerType !== 'touch') hovered = true }
    const leave = () => { hovered = false }
    const wheel = () => { resumeAt = performance.now() + 1000 }
    const nativeDrag = (e: DragEvent) => e.preventDefault()
    const change = () => {
      reduced = motion.matches
      // Stop automatic drift on a whole card, including a preference change
      // mid-drift. A current drag or arrow settle keeps control of its target.
      if (reduced && pointer === null && target === null) {
        if (visible && !document.hidden) settle()
        else settleOnResume = true
      }
    }
    moveRef.current = (direction) => {
      const step = rail.scrollLeft + direction * rail.clientWidth * 0.8
      const snapped = nearest(step, direction)
      // Card by card when starts are known; the old 80 % page step otherwise.
      if (snapped !== null) { position = rail.scrollLeft; target = snapped } else write(step)
      resumeAt = performance.now() + MOUSE_RESUME_MS
    }
    const resize = new ResizeObserver(measure)
    resize.observe(rail)
    if (rail.firstElementChild) resize.observe(rail.firstElementChild)
    const intersection = new IntersectionObserver(([entry]) => {
      if (!entry) return
      visible = entry.isIntersecting
      syncVisibility()
    })
    intersection.observe(rail)
    document.addEventListener('visibilitychange', syncVisibility)
    interaction.addEventListener('pointerenter', enter)
    interaction.addEventListener('pointerleave', leave)
    rail.addEventListener('pointerdown', down)
    window.addEventListener('pointermove', move, { passive: false })
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    rail.addEventListener('click', click, true)
    rail.addEventListener('dragstart', nativeDrag)
    rail.addEventListener('wheel', wheel, { passive: true })
    interaction.addEventListener('focusin', focus)
    interaction.addEventListener('focusout', blur)
    motion.addEventListener('change', change)
    return () => {
      disposed = true
      if (frame !== null) cancelAnimationFrame(frame)
      frame = null
      resize.disconnect(); intersection.disconnect()
      document.removeEventListener('visibilitychange', syncVisibility)
      rail.style.scrollBehavior = old.behavior; rail.style.scrollSnapType = old.snap; rail.style.touchAction = old.touch
      interaction.removeEventListener('pointerenter', enter); interaction.removeEventListener('pointerleave', leave)
      rail.removeEventListener('pointerdown', down); window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', up)
      rail.removeEventListener('click', click, true); rail.removeEventListener('dragstart', nativeDrag)
      rail.removeEventListener('wheel', wheel); interaction.removeEventListener('focusin', focus); interaction.removeEventListener('focusout', blur)
      motion.removeEventListener('change', change); moveRef.current = () => {}
    }
  }, [ref, ready, speed, interactionRef])
  return {
    paused,
    togglePause: () => { pausedRef.current = !pausedRef.current; setPaused(pausedRef.current) },
    move: (direction: number) => moveRef.current(direction),
    /** Attach to the progress bar's fill; the hook writes `scaleX(0–1)` on visible animation frames. */
    progressRef,
  }
}
